import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { workspacesApi, type BrandVoice, type EmailBrandFacts } from '../../services/api'
import type { VoiceGlance } from './useVoiceGlance'
import type { BrandSection, LookStatus } from './brandSections'
import ChatPreview, { PreviewSurfaceControl, type PreviewSurface } from './ChatPreview'
import { DEFAULT_FACTS, describeFacts } from './emailBrandFacts'
import EmailPreview from './EmailPreview'
import { effectiveTraits, formatDay, refusalMessage, traitLine, usableDerivation } from './voiceReview'
import {
  Alert,
  Button,
  Card,
  CardFooter,
  CardHeader,
  CardList,
  Chip,
  ErrorState,
  Eyebrow,
  Hint,
  Inline,
  KeyValue,
  LoadingState,
  Orb,
  SectionLayout,
  SettingRow,
  Stack,
} from '../../ds'

/**
 * Brand · Overview (MAR-384, v6 "Brand" tab) — the state of both halves at once, and where to
 * answer each. Four shapes, all from the server's voice `state`, never inferred:
 *
 * - **idle** → "Not read yet": ask her to read the storefront, or set it yourself.
 * - **deriving** → "Reading": the honest wait, bounded by the server's six-minute window.
 * - **proposed / confirmed** → the overview: what she read, each side's status, and what only
 *   the merchant can state.
 *
 * ⚠️ What this page does NOT do, against the design:
 *
 * - It never runs Shopify brand detection. Detection runs when How we look OPENS (MAR-334:
 *   "a later Shopify change becomes a new suggestion the next time Brand styling opens"), so
 *   the look row shows the LIVE facts. Its status chip is `lookStatus` — the STORED observation
 *   (a GET that runs no detection, MAR-391) until detection has run this visit, then its verdict.
 * - No "one set of facts for emails and chat": chat reads only the colour and paper
 *   (`ChatPreview`), so the row says that, and only when styling is set — with nothing stored,
 *   chat keeps Marva's own look rather than these defaults.
 */

interface BrandOverviewProps {
  nav: ReactNode
  glance: VoiceGlance
  /** How we look's status: the stored observation, or detection's verdict once it ran (`lookStatusOf`). */
  lookStatus?: LookStatus
  open: (section: BrandSection) => void
  /** Owner/admin: the email preview routes refuse anyone else (`authorizeEmailStyleAccess`). */
  canPreview: boolean
  /**
   * Is the overview the open section? It stays MOUNTED, hidden, while another is open (the
   * Brand tab's `SectionPanel`), so a styling save on How we look would leave its live look
   * stale. It re-reads that look when it is shown again — never while hidden. @default true
   */
  active?: boolean
}

/** The live look and its preview, stamped with the workspace they belong to. */
interface LookSnapshot {
  workspaceId: string
  /**
   * Whether the preview was ASKED for. ⚠️ Part of the stamp: the role resolves after the first
   * render, so `canPreview` flips false→true, and a snapshot taken without the preview would
   * otherwise render "could not be drawn" until the second read lands.
   */
  canPreview: boolean
  /** The styling it was read against (`stylesKeyOf`) — a save elsewhere makes it stale. */
  stylesKey: string
  /** `undefined` = nothing stored yet (defaults); `null` = the read failed. */
  facts: EmailBrandFacts | undefined | null
  previewHtml: string | null
  /** True when the store has no abandoned cart to render — said, never faked. */
  noCart: boolean
}

/**
 * The styling the store holds now. Every styling write in the app (`EmailStylesSection`'s save
 * and Shopify confirm) puts the saved workspace into the store, so a change here is a change on
 * the server this overview has not read yet.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stylesKeyOf = (workspace: any): string => JSON.stringify(workspace?.emailStyles ?? null)

const useLiveLook = (workspaceId: string | undefined, canPreview: boolean, stylesKey: string, shown: boolean): LookSnapshot | null => {
  const [snap, setSnap] = useState<LookSnapshot | null>(null)
  /** What was last ASKED for, so being shown again without a change reads nothing. */
  const asked = useRef<string | null>(null)
  useEffect(() => {
    if (!workspaceId || !shown) return
    const key = JSON.stringify([workspaceId, canPreview, stylesKey])
    if (asked.current === key) return
    asked.current = key
    let active = true
    let settled = false
    const done = (next: LookSnapshot) => {
      settled = true
      if (active) setSnap(next)
    }
    Promise.all([
      workspacesApi.get(workspaceId).catch(() => ({ success: false, data: undefined })),
      canPreview ? workspacesApi.getEmailPreviewCart(workspaceId).catch(() => ({ success: false, data: undefined })) : null,
    ]).then(async([workspace, cart]) => {
      const facts = workspace.success && workspace.data
        ? ((workspace.data as { emailStyles?: EmailBrandFacts }).emailStyles ?? undefined)
        : null
      const cartId = cart?.success && cart.data ? cart.data.cartId : null
      const base = { workspaceId, canPreview, stylesKey, facts }
      // ⚠️ A FAILED styling read is not "nothing stored": rendering DEFAULT_FACTS here would show
      // Marva's defaults under "by the same renderer a shopper gets" when the shopper may well
      // get the merchant's own styling. No preview is drawn rather than a wrong one.
      if (facts === null) return done({ ...base, previewHtml: null, noCart: false })
      if (!cartId) return done({ ...base, previewHtml: null, noCart: canPreview })
      try {
        const res = await workspacesApi.previewEmailStyle(workspaceId, facts ? { ...DEFAULT_FACTS, ...facts } : DEFAULT_FACTS, false, cartId)
        done({ ...base, previewHtml: res.success ? res.data?.htmlBody ?? null : null, noCart: false })
      } catch {
        done({ ...base, previewHtml: null, noCart: false })
      }
    })
    return () => {
      active = false
      // A read torn down before it landed was never answered: let the next run ask again.
      if (!settled && asked.current === key) asked.current = null
    }
  }, [workspaceId, canPreview, stylesKey, shown])
  return snap && snap.workspaceId === workspaceId && snap.canPreview === canPreview && snap.stylesKey === stylesKey ? snap : null
}

/** "Not set" for an empty value, so an absent fact is said rather than hidden. */
const orNotSet = (value: string | undefined | null) => (value && value.trim() ? value.trim() : 'Not set')
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * What she cannot read off a shop — the merchant-stated fields, read from the gated live voice
 * and the workspace. ⚠️ A failed voice read is "could not be read", never "Not set": the
 * merchant may well have stated them, and saying otherwise invites them to type them again.
 */
const statedRows = (live: BrandVoice | null, liveFailed: boolean, assistantName: string) => {
  const unknown = 'Could not be read just now'
  const words = live?.forbiddenWords ?? []
  const examples = live?.exampleResponses ?? []
  const name = assistantName.trim()
  const items = [
    { label: 'Words to avoid', value: liveFailed ? unknown : words.length ? words.join(', ') : 'Not set' },
    { label: 'Greeting', value: liveFailed ? unknown : orNotSet(live?.requiredPhrases?.greeting) },
    { label: 'Sign-off', value: liveFailed ? unknown : orNotSet(live?.requiredPhrases?.signOff) },
    { label: 'Example replies', value: liveFailed ? unknown : examples.length ? count(examples.length, 'example', 'examples') : 'Not set' },
    // Blank is not unset-and-broken: it means she signs as Marva, and the row says so.
    { label: 'Assistant name', value: name || 'Not set — she signs as Marva' },
  ]
  const nothingSet = !liveFailed && !words.length && !live?.requiredPhrases?.greeting?.trim() &&
    !live?.requiredPhrases?.signOff?.trim() && !examples.length && !name
  return { items, nothingSet }
}

export default function BrandOverview({ nav, glance, lookStatus, open, canPreview, active = true }: BrandOverviewProps) {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assistantName = (((currentWorkspace as any)?.assistantName ?? '') as string)
  const look = useLiveLook(workspaceId, canPreview, stylesKeyOf(currentWorkspace), active)
  const [surface, setSurface] = useState<PreviewSurface>('email')
  const snapshot = glance.snapshot
  const review = snapshot?.review ?? null
  const state = review?.state

  const emailPreview = (
    <>
      {!look
        ? <LoadingState caption="Loading the preview…" />
        : look.previewHtml
          ? <EmailPreview html={look.previewHtml} height={520} compactHeight={460} />
          : look.noCart
            ? <Hint>No abandoned cart is available yet. Marva will use your current styling when one is.</Hint>
            : <Hint>The preview could not be drawn just now. Nothing about your styling changed.</Hint>}
      {look?.previewHtml && (
        <Hint>Built from the newest real abandoned cart in this store, by the same renderer a shopper gets.</Hint>
      )}
    </>
  )
  // The live facts, as the widget reads them: `undefined` (nothing stored) keeps its own look.
  // ⚠️ A FAILED read is not "nothing stored" — drawing Marva's look then could be wrong.
  const chatPreview = !look
    ? <LoadingState caption="Loading the preview…" />
    : look.facts === null
      ? <Hint>The preview could not be drawn just now. Nothing about your styling changed.</Hint>
      : <ChatPreview facts={look.facts} assistantName={assistantName} />

  const previewAside = (title: string) => !canPreview ? null : (
    <Card>
      <Stack gap="sm">
        <Inline justify="between">
          <Eyebrow tone="muted">{title}</Eyebrow>
          <PreviewSurfaceControl value={surface} onChange={setSurface} />
        </Inline>
        {surface === 'chat' ? chatPreview : emailPreview}
      </Stack>
    </Card>
  )

  const frame = (main: ReactNode, aside: ReactNode) => (
    <SectionLayout nav={nav} aside={aside} asideLabel="Preview" asideWidth="wide">
      <Stack>{main}</Stack>
    </SectionLayout>
  )

  if (!workspaceId) return null
  if (!snapshot) return frame(<LoadingState caption="Reading what she has worked out…" />, null)
  if (snapshot.failed || !review) {
    return frame(
      <ErrorState title="Could not read what she has worked out about your brand." onRetry={glance.reload}>
        Nothing is shown rather than a guess.
      </ErrorState>,
      null
    )
  }

  const errorNote = glance.error && <Alert tone="danger" live="assertive">{glance.error}</Alert>

  // ── Not read yet (Brand-1) ─────────────────────────────────────────────────────────────
  if (state === 'idle') {
    const attempted = review.derivationRequest?.requestedAt
    return frame(
      <>
        <Card>
          <CardHeader
            icon={<Orb size={34} />}
            title={<>She reads your store and writes down <span className="mv-em">how you sound.</span></>}
            description={attempted
              ? `She started reading your storefront on ${formatDay(attempted)} and it did not finish, so there is nothing to show you. Ask her again and she will try from the start.`
              : 'She has not read your storefront yet. Nothing here is derived, learned or guessed — ask her to read it and she will write down how you sound, in her words, for you to correct.'}
          />
          <CardList>{errorNote}</CardList>
          <CardFooter>
            <Button size="sm" busy={glance.deriving} busyLabel="Asking her…" onClick={glance.derive}>
              Read my storefront
            </Button>
            <Button variant="ghost" size="sm" onClick={() => open('sound')}>
              Set it myself instead
            </Button>
          </CardFooter>
        </Card>
        <Card padding="sm">
          <SettingRow title="From your storefront" description="Tone, formality, emoji and length, with a sentence on how you sound." />
          <SettingRow
            title="From your Shopify brand"
            description="Logo and brand colour, read when you open How we look. Typeface, corners and paper start as defaults she tells you about."
          />
        </Card>
      </>,
      previewAside('Live today')
    )
  }

  // ── Reading (Brand-2) ──────────────────────────────────────────────────────────────────
  if (state === 'deriving') {
    return frame(
      <Card>
        <CardHeader
          icon={<Orb size={34} />}
          title="Reading your store"
          description={glance.gaveUp
            ? 'She has been reading your storefront for over six minutes, which is longer than it takes. Nothing has been written, and nothing was changed — ask her again when you are ready.'
            : 'This takes a couple of minutes, and you can leave this page — it carries on without you.'}
          aside={glance.gaveUp && (
            <Button variant="ghost" size="sm" busy={glance.deriving} busyLabel="Asking her…" onClick={glance.derive}>
              Try again
            </Button>
          )}
        />
        <CardList>
          {errorNote}
          <SettingRow title="Your storefront" description="Reading your own copy for how you sound." aside={glance.gaveUp ? 'Stopped' : 'Reading…'} />
          <SettingRow
            title="Your Shopify brand"
            description="Read when you open How we look, and shown there for you to confirm."
            aside={<Button variant="link" onClick={() => open('look')}>Review</Button>}
          />
          <Hint>If it runs past six minutes she stops and says so. Nothing is written and nothing changes until you confirm.</Hint>
        </CardList>
      </Card>,
      previewAside('Live today')
    )
  }

  // ── Overview (Main) ────────────────────────────────────────────────────────────────────
  const derived = usableDerivation(review.derived)
  const confirmed = state === 'confirmed'
  const stated = statedRows(snapshot.live, snapshot.liveFailed, assistantName)
  const lookFacts = look?.facts

  const readCard = derived ? (
    <Card>
      <CardHeader
        icon={<Orb size={34} />}
        title="She read your store"
        description={
          `Your storefront, ${derived.provenance.sourceDomain}, for how you sound — ` +
          `${count(derived.provenance.pageCount, 'page', 'pages')} of your own copy, ${formatDay(derived.provenance.derivedAt)}.` +
          (confirmed ? ` You confirmed it on ${formatDay(derived.confirmedAt)}.` : ' Nothing changes for shoppers until you confirm.')
        }
        aside={
          <Button variant="ghost" size="sm" busy={glance.deriving} busyLabel="Asking her…" onClick={glance.derive}>
            Read it again
          </Button>
        }
      />
      {errorNote && <CardList>{errorNote}</CardList>}
    </Card>
  ) : (
    // A row that says proposed/confirmed with a block nobody can render: the same sentence the
    // server says when it refuses to confirm one, and the one act that clears it.
    <Card>
      <CardHeader
        title="How I think you sound"
        description={refusalMessage('derivation_invalid', '')}
        aside={
          <Button size="sm" busy={glance.deriving} busyLabel="Asking her…" onClick={glance.derive}>
            Read it again
          </Button>
        }
      />
      {errorNote && <CardList>{errorNote}</CardList>}
    </Card>
  )

  const lookChip = lookStatus === 'proposed'
    ? <Chip tone="gold" dot>Awaiting your confirmation</Chip>
    // ⚠️ Not "Confirmed": the server answers `current` also when the styles were saved by hand
    // and Shopify offered no evidence to confirm (`workspaces` detection) — up to date, not agreed.
    : lookStatus === 'current' ? <Chip tone="sage" dot>Up to date</Chip>
      : lookStatus === 'unread' ? <Chip>Not read</Chip> : null

  return frame(
    <>
      {readCard}
      {/* A <section> so the name is announced: a label on the role-less <div> is ignored. */}
      <Card as="section" padding="sm" aria-label="What she found">
        {derived && (
          <SettingRow
            data-testid="overview-sound"
            title={
              <Inline>
                <span>How we sound</span>
                <Chip tone={confirmed ? 'sage' : 'gold'} dot>{confirmed ? 'Confirmed' : 'Awaiting your confirmation'}</Chip>
              </Inline>
            }
            description={
              <Stack gap="sm">
                <p className="t-voice">{derived.traits.summary}</p>
                <span>{traitLine(confirmed ? effectiveTraits(derived, review.fieldSources, snapshot.live) : derived.traits)}</span>
              </Stack>
            }
            aside={<Button variant="link" onClick={() => open('sound')}>Review</Button>}
            align="top"
          />
        )}
        <SettingRow
          data-testid="overview-look"
          title={<Inline><span>How we look</span>{lookChip}</Inline>}
          description={
            <Stack gap="sm">
              <span>
                {!look ? 'Reading your current styling…'
                  : lookFacts === null ? 'Your current styling could not be read just now.'
                    : lookFacts === undefined ? `${describeFacts(DEFAULT_FACTS)} — Marva's defaults, nothing set yet`
                      : describeFacts({ ...DEFAULT_FACTS, ...lookFacts })}
              </span>
              <Hint>
                {lookFacts ? 'What your emails use today; chat takes its colour and paper.' : 'What your emails use today.'}{' '}
                {lookStatus === 'unread'
                  ? 'She has not read your Shopify brand yet — she does when you open How we look.'
                  : 'She checks your Shopify brand for changes when you open How we look.'}
              </Hint>
            </Stack>
          }
          aside={<Button variant="link" onClick={() => open('look')}>Review</Button>}
          align="top"
        />
        {/* The list sits `below` the row rather than in its description, so its rules run the card's
            full width like the dividers above instead of stopping short of the Edit column. */}
        <SettingRow
          data-testid="overview-stated"
          title={<Inline><span>What she can&apos;t read off a shop</span>{stated.nothingSet && <Chip>Not set</Chip>}</Inline>}
          aside={<Button variant="link" onClick={() => open('sound')}>{stated.nothingSet ? 'Add them' : 'Edit'}</Button>}
          below={<KeyValue items={stated.items} />}
        />
      </Card>
      {(!confirmed || lookStatus === 'proposed') && (
        <Hint>Your current voice and styling stay live until you confirm each one.</Hint>
      )}
    </>,
    previewAside('Preview · real abandoned cart')
  )
}
